import type { Deliverable, Project } from '../types/portal'
import { deliverableStage } from './deliverables'

/**
 * The project's headline status, derived from its deliverables so Portal and
 * client can never see contradictory states.
 */
export function projectStatus(project: Project): string {
  const items = project.deliverables

  if (project.archived) return 'Complete'
  if (items.length === 0) return 'Planned'
  if (items.every((item) => item.status === 'Delivered')) return 'Complete'

  if (items.some((item) => item.status === 'Ready for Review')) return 'Awaiting Client Review'

  /* Only genuinely outstanding feedback counts. Completed requests are history
     and must never keep a project flagged as needing changes. */
  if (items.some((item) => item.changeRequests.some((request) => !request.resolved))) {
    return 'Changes in Progress'
  }

  if (items.some((item) => item.status === 'Changes Requested')) return 'Changes in Progress'

  /* Stage follows status, so the project reads from the furthest work along. */
  if (items.some((item) => item.status === 'Editing')) return 'Editing'

  if (items.some((item) => item.status === 'In Progress')) return 'Filming'

  if (items.some((item) => deliverableStage(item.status) === 'Final Delivery')) {
    return 'Final Delivery'
  }

  return 'Planned'
}

/**
 * A show's headline status, in the exact same priority order as
 * `projectStatus` above, but over every deliverable across every one of its
 * episodes at once — a show is "Awaiting Client Review" the moment any
 * episode is, "Complete" only once every episode is, and so on. Lets the Show
 * Admin header carry the same status/last-updated line a single project's
 * own admin page already does, instead of just an episode count.
 */
export function showStatus(episodes: Project[]): string {
  const active = episodes.filter((row) => !row.archived)

  if (episodes.length === 0) return 'Planned'
  if (active.length === 0) return 'Complete'

  const items = active.flatMap((row) => row.deliverables)
  if (items.length === 0) return 'Planned'
  if (items.every((item) => item.status === 'Delivered')) return 'Complete'

  if (items.some((item) => item.status === 'Ready for Review')) return 'Awaiting Client Review'

  if (items.some((item) => item.changeRequests.some((request) => !request.resolved))) {
    return 'Changes in Progress'
  }

  if (items.some((item) => item.status === 'Changes Requested')) return 'Changes in Progress'

  if (items.some((item) => item.status === 'Editing')) return 'Editing'

  if (items.some((item) => item.status === 'In Progress')) return 'Filming'

  if (items.some((item) => deliverableStage(item.status) === 'Final Delivery')) {
    return 'Final Delivery'
  }

  return 'Planned'
}

const STAMP_MONTHS: Record<string, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
}

/** Parses the "DD MON YYYY" stamp `Project.updated` is stored in (e.g. "07
    SEP 2026") into a comparable timestamp. Returns -1 for an empty or
    unrecognised value, so it never wins a comparison against a real one. */
function stampValue(value: string): number {
  const match = /^(\d{2}) (\w{3}) (\d{4})$/.exec(value)
  if (!match) return -1
  const month = STAMP_MONTHS[match[2].toUpperCase()]
  if (month === undefined) return -1
  return new Date(Number(match[3]), month, Number(match[1])).getTime()
}

/**
 * The most recent `updated` stamp across every episode of a show, so the
 * Show Admin header can say when the show — as a whole — last changed,
 * rather than only ever showing a per-episode date one level down.
 */
export function showUpdated(episodes: Project[]): string {
  let latest = ''
  let latestValue = -1
  for (const row of episodes) {
    const value = stampValue(row.updated)
    if (value > latestValue) {
      latestValue = value
      latest = row.updated
    }
  }
  return latest
}

export interface ClientHeadline {
  heading: 'Your Next Action' | 'Project Status'
  message: string
  support: string
  /** The deliverable the client should open, when there is one. */
  actionId?: string
  actionLabel?: string
  /** True when the action button should open a LIST rather than a specific
      deliverable — set whenever more than one deliverable is awaiting
      review, since `actionId` in that case is only ever the first of
      several and opening it directly leaves a client wondering what the
      other ones were. `Home.tsx` and `ShowHome.tsx` read this to decide
      whether their action button opens `actionId` or navigates to the
      Deliverables list instead. */
  multiple?: boolean
  /** Which episode `actionId` belongs to — set only by `showClientHeadline`
      below, where a deliverable can belong to any episode of the show, not
      just whichever one happens to be open. Absent for a single project's
      own headline, where that's always the current one. */
  actionProjectId?: string
}

const NOTHING_REQUIRED = 'Nothing is required from you right now.'

/**
 * Derives the client's headline from the real state of the project, in the
 * priority order Portal defined: client actions first, then project progress
 * from furthest-along to earliest. Every bucket below is count-aware — a
 * status shared by several deliverables reads as a count, never just the
 * name of whichever one happens to be first.
 */
export function clientHeadline(
  project: Project,
  visible: Deliverable[],
  complete: boolean,
): ClientHeadline {
  const awaiting = visible.filter((item) => item.status === 'Ready for Review')

  if (awaiting.length === 1) {
    const item = awaiting[0]
    return {
      heading: 'Your Next Action',
      message: `${item.name} is ready for your review.`,
      support: item.version
        ? `Please review ${item.version} and approve it or request changes.`
        : 'Please review the latest version and approve it or request changes.',
      actionId: item.id,
      actionLabel: item.version ? `Review ${item.version}` : `Review ${item.name}`,
    }
  }

  if (awaiting.length > 1) {
    return {
      heading: 'Your Next Action',
      message: `${awaiting.length} deliverables are ready for your review.`,
      support: 'Please review the latest versions and approve them or request changes.',
      actionId: awaiting[0].id,
      actionLabel: 'Start Reviewing',
      multiple: true,
    }
  }

  if (complete) {
    return {
      heading: 'Project Status',
      message: 'Your project is complete.',
      support: 'All final files are ready.',
    }
  }

  const changing = visible.filter((item) => item.status === 'Changes Requested')

  if (changing.length === 1) {
    return {
      heading: 'Project Status',
      message: `Changes Requested: ${changing[0].name}.`,
      support: 'We’ll let you know when the next version is ready.',
    }
  }

  if (changing.length > 1) {
    return {
      heading: 'Project Status',
      message: `Changes Requested: ${changing.length} deliverables.`,
      support: 'We’ll let you know when the next versions are ready.',
    }
  }

  /* Each of these is simply what the status means: Editing and In Progress are
     both production underway — Editing is further along, so it takes priority
     when a series has some of each — and Planned is pre-production. */
  const editing = visible.filter((item) => item.status === 'Editing')

  if (editing.length === 1) {
    return {
      heading: 'Project Status',
      message: `Editing: ${editing[0].name}.`,
      support: 'We’ll let you know when it’s ready for review.',
    }
  }

  if (editing.length > 1) {
    return {
      heading: 'Project Status',
      message: `Editing: ${editing.length} deliverables.`,
      support: 'We’ll let you know as each one is ready for review.',
    }
  }

  const filming = visible.filter((item) => item.status === 'In Progress')

  if (filming.length === 1) {
    return {
      heading: 'Project Status',
      message: `Filming: ${filming[0].name}.`,
      support: 'We’ll let you know once it moves into editing.',
    }
  }

  if (filming.length > 1) {
    return {
      heading: 'Project Status',
      message: `Filming: ${filming.length} deliverables.`,
      support: 'We’ll let you know as each one moves into editing.',
    }
  }

  const planned = visible.filter((item) => item.status === 'Planned')

  if (planned.length === 1) {
    return {
      heading: 'Project Status',
      message: `Pre-Production: ${planned[0].name}.`,
      support: NOTHING_REQUIRED,
    }
  }

  if (planned.length > 1) {
    return {
      heading: 'Project Status',
      message: `Pre-Production: ${planned.length} deliverables.`,
      support: NOTHING_REQUIRED,
    }
  }

  /* Everything signed off, but the finals are not out yet. */
  const allApproved =
    visible.length > 0 &&
    visible.every((item) => deliverableStage(item.status) === 'Final Delivery')
  const finalsPending = visible.some((item) => item.status === 'Approved')

  if (allApproved && finalsPending) {
    return {
      heading: 'Project Status',
      message: 'Everything is approved. We’re preparing your final files.',
      support: NOTHING_REQUIRED,
    }
  }

  const approved = visible.filter((item) => item.status === 'Approved')

  if (approved.length === 1) {
    return {
      heading: 'Project Status',
      message: `Approved: ${approved[0].name}.`,
      support: NOTHING_REQUIRED,
    }
  }

  if (approved.length > 1) {
    return {
      heading: 'Project Status',
      message: `Approved: ${approved.length} deliverables.`,
      support: NOTHING_REQUIRED,
    }
  }

  if (visible.length === 0) {
    return {
      heading: 'Project Status',
      message: 'We’re setting your project up.',
      support: NOTHING_REQUIRED,
    }
  }

  return {
    heading: 'Project Status',
    message: `${project.name} is underway.`,
    support: NOTHING_REQUIRED,
  }
}

interface TaggedDeliverable {
  item: Deliverable
  episode: Project
}

/**
 * The same headline a single project's Home page shows, but for the Show
 * Home landing page — pooling every visible deliverable across every
 * non-archived episode of the show, in the exact same priority order as
 * `clientHeadline` above. A specific deliverable's message always names the
 * episode it belongs to, since a show reader can't assume "the project" the
 * way a single-project reader can, and `actionProjectId` carries that
 * episode so the action button can switch to it before opening the
 * deliverable.
 */
export function showClientHeadline(episodes: Project[]): ClientHeadline {
  const active = episodes.filter((row) => !row.archived)
  const pool: TaggedDeliverable[] = active.flatMap((episode) =>
    episode.deliverables
      .filter((item) => item.visibleToClient)
      .map((item) => ({ item, episode })),
  )
  const complete = showStatus(episodes) === 'Complete'

  const awaiting = pool.filter((entry) => entry.item.status === 'Ready for Review')

  if (awaiting.length === 1) {
    const { item, episode } = awaiting[0]
    return {
      heading: 'Your Next Action',
      message: `${item.name} (${episode.name}) is ready for your review.`,
      support: item.version
        ? `Please review ${item.version} and approve it or request changes.`
        : 'Please review the latest version and approve it or request changes.',
      actionId: item.id,
      actionProjectId: episode.id,
      actionLabel: item.version ? `Review ${item.version}` : `Review ${item.name}`,
    }
  }

  if (awaiting.length > 1) {
    const episodeCount = new Set(awaiting.map((entry) => entry.episode.id)).size
    return {
      heading: 'Your Next Action',
      message:
        episodeCount === 1
          ? `${awaiting.length} deliverables in ${awaiting[0].episode.name} are ready for your review.`
          : `${awaiting.length} deliverables across ${episodeCount} episodes are ready for your review.`,
      support: 'Please review the latest versions and approve them or request changes.',
      actionId: awaiting[0].item.id,
      actionProjectId: awaiting[0].episode.id,
      actionLabel: 'Start Reviewing',
      multiple: true,
    }
  }

  if (complete) {
    return {
      heading: 'Project Status',
      message: 'Your show is complete.',
      support: 'All final files are ready.',
    }
  }

  const changing = pool.filter((entry) => entry.item.status === 'Changes Requested')

  if (changing.length === 1) {
    return {
      heading: 'Project Status',
      message: `Changes Requested: ${changing[0].item.name} (${changing[0].episode.name}).`,
      support: 'We’ll let you know when the next version is ready.',
    }
  }

  if (changing.length > 1) {
    return {
      heading: 'Project Status',
      message: `Changes Requested: ${changing.length} deliverables.`,
      support: 'We’ll let you know when the next versions are ready.',
    }
  }

  const editing = pool.filter((entry) => entry.item.status === 'Editing')

  if (editing.length === 1) {
    return {
      heading: 'Project Status',
      message: `Editing: ${editing[0].item.name} (${editing[0].episode.name}).`,
      support: 'We’ll let you know when it’s ready for review.',
    }
  }

  if (editing.length > 1) {
    return {
      heading: 'Project Status',
      message: `Editing: ${editing.length} deliverables.`,
      support: 'We’ll let you know as each one is ready for review.',
    }
  }

  const filming = pool.filter((entry) => entry.item.status === 'In Progress')

  if (filming.length === 1) {
    return {
      heading: 'Project Status',
      message: `Filming: ${filming[0].item.name} (${filming[0].episode.name}).`,
      support: 'We’ll let you know once it moves into editing.',
    }
  }

  if (filming.length > 1) {
    return {
      heading: 'Project Status',
      message: `Filming: ${filming.length} deliverables.`,
      support: 'We’ll let you know as each one moves into editing.',
    }
  }

  const planned = pool.filter((entry) => entry.item.status === 'Planned')

  if (planned.length === 1) {
    return {
      heading: 'Project Status',
      message: `Pre-Production: ${planned[0].item.name} (${planned[0].episode.name}).`,
      support: NOTHING_REQUIRED,
    }
  }

  if (planned.length > 1) {
    const episodeCount = new Set(planned.map((entry) => entry.episode.id)).size
    return {
      heading: 'Project Status',
      message:
        episodeCount === 1
          ? `Pre-Production: ${planned.length} deliverables in ${planned[0].episode.name}.`
          : `Pre-Production: ${planned.length} deliverables across ${episodeCount} episodes.`,
      support: NOTHING_REQUIRED,
    }
  }

  const allApproved =
    pool.length > 0 &&
    pool.every((entry) => deliverableStage(entry.item.status) === 'Final Delivery')
  const finalsPending = pool.some((entry) => entry.item.status === 'Approved')

  if (allApproved && finalsPending) {
    return {
      heading: 'Project Status',
      message: 'Everything is approved. We’re preparing your final files.',
      support: NOTHING_REQUIRED,
    }
  }

  const approved = pool.filter((entry) => entry.item.status === 'Approved')

  if (approved.length === 1) {
    return {
      heading: 'Project Status',
      message: `Approved: ${approved[0].item.name} (${approved[0].episode.name}).`,
      support: NOTHING_REQUIRED,
    }
  }

  if (approved.length > 1) {
    return {
      heading: 'Project Status',
      message: `Approved: ${approved.length} deliverables.`,
      support: NOTHING_REQUIRED,
    }
  }

  if (pool.length === 0) {
    return {
      heading: 'Project Status',
      message: 'We’re setting your show up.',
      support: NOTHING_REQUIRED,
    }
  }

  return {
    heading: 'Project Status',
    message: 'This show is underway.',
    support: NOTHING_REQUIRED,
  }
}
